import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Bell, MessageSquare, PhoneIncoming, UserPlus, ArrowRightLeft, Sparkles, X,
} from "lucide-react";

const ICONS = {
  sms_in:       { Icon: MessageSquare,  cls: "text-blue-600 bg-blue-50" },
  call_in:      { Icon: PhoneIncoming,  cls: "text-emerald-600 bg-emerald-50" },
  lead_new:     { Icon: UserPlus,       cls: "text-violet-600 bg-violet-50" },
  crm_move:     { Icon: ArrowRightLeft, cls: "text-amber-600 bg-amber-50" },
  ai_qualified: { Icon: Sparkles,       cls: "text-fuchsia-600 bg-fuchsia-50" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 25 } });
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* silent — never break the shell */ }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0);
      try { await api.post("/notifications/mark-read", {}); } catch { /* silent */ }
    }
  };

  const openItem = (n) => {
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        data-testid="notification-bell"
        className="relative h-10 w-10 rounded-full flex items-center justify-center hover:bg-[var(--app-surface)] transition-colors"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] rounded-2xl border border-[var(--app-line)] bg-white shadow-xl z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--app-line)]">
            <div className="font-display text-sm font-bold">Notifications</div>
            <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-black">
              <X size={15} />
            </button>
          </div>
          <div className="max-h-[70vh] overflow-y-auto" data-testid="notification-list">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-xs text-muted-foreground">
                You&rsquo;re all caught up.
              </div>
            ) : (
              items.map((n) => {
                const { Icon, cls } = ICONS[n.type] || { Icon: Bell, cls: "text-neutral-600 bg-neutral-100" };
                return (
                  <button
                    key={n.id}
                    onClick={() => openItem(n)}
                    data-testid={`notification-item-${n.id}`}
                    className={`w-full text-left flex gap-3 px-4 py-3 border-b border-[var(--app-line)] last:border-b-0 hover:bg-[var(--app-surface)] transition-colors ${n.read ? "" : "bg-blue-50/40"}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${cls}`}>
                      <Icon size={15} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{n.title}</div>
                      {n.body && <div className="text-xs text-muted-foreground truncate">{n.body}</div>}
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                        {timeAgo(n.created_at)}
                      </div>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-blue-600 shrink-0 mt-2" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
